/**
 * Read a price-change delta for the leader market of an event.
 *
 * Strategy per timeframe:
 *  - 1h:  gamma `oneHourPriceChange` -> fallback yesHistory[-1] vs [-2]
 *  - 24h: gamma `oneDayPriceChange`  -> fallback yesHistory[-1] vs [-25]
 *  - 7d:  yesHistory[-1] vs [0]
 *
 * Gamma omits the 1h/24h fields on low-activity markets, so without
 * the yesHistory fallback most rows would render as "–". The sync
 * worker stores 168 hourly points in yesHistory (7d × 24h), so the
 * fallback is exact for the 1h slot and a tight approximation for
 * the 24h slot.
 *
 * Returns the delta in price units (0..1 probability points), or
 * `null` when the data is genuinely missing so the UI can render "–"
 * instead of fabricating a 0%.
 */
export function getEventPriceChange(
  event: any,
  timeframe: '1h' | '24h' | '7d',
): number | null {
  const main = event?.markets?.[0];

  // Pick whichever history array has actual data. Backend ships event-
  // level history empty (`[]`) for many futures while the leader sub-
  // market carries the real points; breaking-category events use a
  // separate `history` field (shape `[{t,p}]`) instead of `yesHistory`.
  // `??` would lock onto `[]` because it's truthy and only nullish-
  // falls-through, so do an explicit length check across all sources.
  const candidates = [
    (event as any)?.yesHistory,
    main?.yesHistory,
    (event as any)?.history,
    main?.history,
  ];
  let hist: any = null;
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) { hist = c; break; }
  }
  const histPoints: number[] = Array.isArray(hist)
    ? hist
        .map((p: any): number | null => {
          const v = typeof p === 'number' ? p : (p?.p ?? p?.price ?? p?.value ?? p?.y);
          const n = Number(v);
          if (!Number.isFinite(n)) return null;
          return n > 1 ? n / 100 : n;
        })
        .filter((v): v is number => v != null && v >= 0 && v <= 1)
    : [];

  const deltaFromHistory = (offset: number): number | null => {
    if (histPoints.length < offset + 1) return null;
    const last = histPoints[histPoints.length - 1];
    const prev = histPoints[histPoints.length - 1 - offset];
    if (last == null || prev == null) return null;
    return last - prev;
  };

  const firstFinite = (...vals: any[]): number | null => {
    for (const v of vals) {
      const n = typeof v === 'number' ? v : (v != null ? Number(v) : NaN);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  if (timeframe === '1h') {
    // Breaking-category events flatten the delta onto the event itself
    // (`m.oneDayPriceChange` -> event.oneDayPriceChange in fetchEvents).
    // Trending/events ship it on the leader sub-market. Look at both.
    const direct = firstFinite(
      (event as any)?.oneHourPriceChange,
      main?.oneHourPriceChange,
    );
    if (direct != null) return direct;
    return deltaFromHistory(1);
  }
  if (timeframe === '24h') {
    const direct = firstFinite(
      (event as any)?.oneDayPriceChange,
      (event as any)?.priceChange24h,
      (event as any)?.priceChange,
      main?.oneDayPriceChange,
      main?.priceChange24h,
      main?.priceChange,
    );
    if (direct != null) return direct;
    return deltaFromHistory(24);
  }
  // 7d: first vs last of the available history. Returns null when
  // there are no real history points (the random-noise sparkline
  // fallback used by the chart cell never reaches this helper).
  if (histPoints.length < 2) return null;
  return histPoints[histPoints.length - 1] - histPoints[0];
}
